import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, Car, ShieldCheck, Bell, Search, Wallet, Camera,
  Check, PlusCircle, FileText
} from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import { ownerService } from '../services/owner.service';
import { PRICING_TIER_OPTIONS, normalizePricingConfigurations } from '../utils/pricing';
import { showAppAlert } from '../utils/appAlert';
import { buildPublicFileUrl } from '../utils/fileUrl';

const PHONE_PATTERN = /(?:\+?\d[\s-.]?)?(?:\(?\d{1,4}\)?[\s-.]?)?(?:\d[\s-.]?){6,}\d/;
const NUMBER_WORDS_PATTERN = /(?:zero|one|two|three|four|five|six|seven|eight|nine)(?:[\s,-]+(?:zero|one|two|three|four|five|six|seven|eight|nine)){5,}/i;

const AddVehicle = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const draftId = searchParams.get('id');

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [selectedPricingType, setSelectedPricingType] = useState('DAILY');
  const [newPricingAmount, setNewPricingAmount] = useState('');

  const [options, setOptions] = useState({
    makes: [],
    categories: [],
    cities: [],
    specifications: []
  });

  const [formData, setFormData] = useState({
    id: draftId || null,
    makeId: '',
    model: '',
    plateNumber: '',
    year: '2024',
    odometer: '',
    transmission: '',
    fuelType: '',
    seats: '',
    description: '',
    categoryId: '',
    cityId: '',
    pricing: {
      outsideCityPrice: '',
      professionalDriver: false,
      allowOutsideCity: true,
      pricingConfigurations: []
    },
    specificationIds: [],
    heroImageUrl: '',
    primaryImageUrl: '',
    images: [],
    documents: [],
    status: '',
    rejectionReason: ''
  });

  useEffect(() => {
    const initData = async () => {
      try {
        setLoading(true);
        const fetchedOptions = await ownerService.getCarOptions();
        setOptions(fetchedOptions);

        if (draftId) {
          const draft = await ownerService.getDraftById(draftId);
          if (draft) {
             setCurrentStep(draft.currentStep || 1);
             setFormData({
                id: draft.id,
                makeId: draft.makeId || '',
                model: draft.model || '',
                plateNumber: draft.plateNumber || '',
                year: draft.year ? draft.year.toString() : '2024',
                odometer: draft.odometer || '',
                transmission: draft.transmission || '',
                fuelType: draft.fuelType || '',
                seats: draft.seats ? draft.seats.toString() : '',
                description: draft.description || '',
                categoryId: draft.categoryId || '',
                cityId: draft.cityId || '',
                pricing: {
                  outsideCityPrice: draft.outsideCityPrice || '',
                  professionalDriver: draft.professionalDriver || false,
                  allowOutsideCity: draft.allowOutsideCity !== false,
                  pricingConfigurations: normalizePricingConfigurations(draft.pricingConfigurations || [])
                },
                specificationIds: draft.specificationIds || [],
                heroImageUrl: draft.heroImageUrl || '',
                primaryImageUrl: draft.primaryImageUrl || '',
                images: draft.images || [],
                documents: draft.documents || [],
                status: draft.status || '',
                rejectionReason: draft.rejectionReason || ''
             });
          }
        }
      } catch (err) {
        console.error("Failed to load options or draft", err);
      } finally {
        setLoading(false);
      }
    };
    initData();
  }, [draftId]);

  const steps = [
    { id: 1, label: 'Vehicle Info' },
    { id: 2, label: 'Pricing & Features' },
    { id: 3, label: 'Media Upload' }
  ];

  const validateStep1 = () => {
    if (!formData.makeId) return 'Car make is required.';
    if (!formData.model.trim()) return 'Model is required.';
    if (!formData.plateNumber.trim()) return 'Plate number is required.';
    if (!formData.year) return 'Year is required.';
    if (!formData.odometer || Number(formData.odometer) < 0) return 'Odometer is required.';
    if (!formData.transmission) return 'Transmission is required.';
    if (!formData.fuelType) return 'Fuel type is required.';
    if (!formData.seats || Number(formData.seats) <= 0) return 'Seats is required.';
    if (formData.description.trim() && (PHONE_PATTERN.test(formData.description) || NUMBER_WORDS_PATTERN.test(formData.description))) {
      return 'Vehicle description cannot contain phone numbers.';
    }
    if (!formData.categoryId) return 'Vehicle category is required.';
    if (!formData.cityId) return 'Car location city is required.';
    return '';
  };

  const validateStep2 = () => {
    if (!formData.pricing.outsideCityPrice || Number(formData.pricing.outsideCityPrice) <= 0) return 'Outside city price is required.';
    const invalidPricing = formData.pricing.pricingConfigurations.find((item) => !item.amount || Number(item.amount) <= 0);
    if (invalidPricing) return `${invalidPricing.label} amount is required.`;
    const configuredTypes = formData.pricing.pricingConfigurations.map((item) => item.pricingType);
    const missingTier = PRICING_TIER_OPTIONS.find((item) => !configuredTypes.includes(item.pricingType));
    if (missingTier) return `${missingTier.label} pricing is required.`;
    return '';
  };

  const handleAddPricingConfiguration = () => {
    if (!selectedPricingType) {
      showAppAlert({ type: 'warning', title: 'Pricing Required', message: 'Please choose a pricing tier.' });
      return;
    }

    if (!newPricingAmount || Number(newPricingAmount) <= 0) {
      showAppAlert({ type: 'warning', title: 'Invalid Amount', message: 'Please enter a valid amount for the selected pricing tier.' });
      return;
    }

    const tierMeta = PRICING_TIER_OPTIONS.find((item) => item.pricingType === selectedPricingType);
    if (!tierMeta) return;

    setFormData((current) => ({
      ...current,
      pricing: {
        ...current.pricing,
        pricingConfigurations: normalizePricingConfigurations([
          ...current.pricing.pricingConfigurations.filter((item) => item.pricingType !== selectedPricingType),
          {
            pricingType: selectedPricingType,
            label: tierMeta.label,
            rangeLabel: tierMeta.rangeLabel,
            perDay: tierMeta.perDay,
            amount: Number(newPricingAmount)
          }
        ])
      }
    }));
    const selectedTypes = formData.pricing.pricingConfigurations.map((item) => item.pricingType);
    const nextAvailablePricing = PRICING_TIER_OPTIONS.find(
      (item) => item.pricingType !== selectedPricingType && !selectedTypes.includes(item.pricingType)
    );
    if (nextAvailablePricing) {
      setSelectedPricingType(nextAvailablePricing.pricingType);
    }
    setNewPricingAmount('');
  };

  const selectedPricingTypes = formData.pricing.pricingConfigurations.map((item) => item.pricingType);
  const availablePricingOptions = PRICING_TIER_OPTIONS.filter(
    (item) => !selectedPricingTypes.includes(item.pricingType)
  );

  const handleRemovePricingConfiguration = (pricingType) => {
    setFormData((current) => ({
      ...current,
      pricing: {
        ...current.pricing,
        pricingConfigurations: current.pricing.pricingConfigurations.filter((item) => item.pricingType !== pricingType)
      }
    }));
  };

  const handlePricingAmountChange = (pricingType, value) => {
    setFormData((current) => ({
      ...current,
      pricing: {
        ...current.pricing,
        pricingConfigurations: current.pricing.pricingConfigurations.map((item) => (
          item.pricingType === pricingType
            ? { ...item, amount: value === '' ? '' : Number(value) }
            : item
        ))
      }
    }));
  };

  const handleNext = async () => {
    try {
      setSaving(true);
      let carId = formData.id;

      // If we don't have a draft ID yet, we must create one.
      if (!carId) {
         const newDraft = await ownerService.createDraft();
         carId = newDraft.id;
         setFormData(prev => ({ ...prev, id: carId }));
      }

      if (currentStep === 1) {
         const step1Error = validateStep1();
         if (step1Error) {
            showAppAlert({ type: 'warning', title: 'Missing Information', message: step1Error });
            setSaving(false);
            return;
         }
         await ownerService.updateDraftStep1(carId, {
            makeId: formData.makeId,
            model: formData.model,
            plateNumber: formData.plateNumber,
            year: parseInt(formData.year),
            categoryId: formData.categoryId,
            cityId: formData.cityId,
            odometer: parseInt(formData.odometer),
            transmission: formData.transmission,
            fuelType: formData.fuelType,
            seats: parseInt(formData.seats),
            description: formData.description
         });
         setCurrentStep(2);
      } else if (currentStep === 2) {
         const step2Error = validateStep2();
         if (step2Error) {
            showAppAlert({ type: 'warning', title: 'Pricing Incomplete', message: step2Error });
            setSaving(false);
            return;
         }
         await ownerService.updateDraftStep2(carId, {
            outsideCityPrice: parseFloat(formData.pricing.outsideCityPrice),
            professionalDriver: formData.pricing.professionalDriver,
            allowOutsideCity: formData.pricing.allowOutsideCity,
            specificationIds: formData.specificationIds,
            pricingConfigurations: formData.pricing.pricingConfigurations.map((item) => ({
              pricingType: item.pricingType,
              amount: Number(item.amount)
            }))
         });
         setCurrentStep(3);
      } else if (currentStep === 3) {
         if (!formData.primaryImageUrl) {
            showAppAlert({ type: 'warning', title: 'Primary Image Required', message: 'A primary car image is required before submitting.' });
            setSaving(false);
            return;
         }
         await ownerService.submitDraft(carId);
         setIsSuccess(true);
      }
    } catch (err) {
       console.error("Error saving step", err);
       showAppAlert({ type: 'error', title: 'Save Failed', message: `Failed to save step: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/owner-dashboard');
    }
  };

  const handleUploadDocument = async (reqDocId, file) => {
    if (!file) return;
    try {
      setSaving(true);
      const updatedDraft = await ownerService.uploadDocument(formData.id, reqDocId, file);
      setFormData(prev => ({ ...prev, documents: updatedDraft.documents }));
    } catch (err) {
      showAppAlert({ type: 'error', title: 'Upload Failed', message: `Failed to upload document: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    try {
      setSaving(true);
      const updatedDraft = await ownerService.deleteDocument(formData.id, docId);
      setFormData(prev => ({ ...prev, documents: updatedDraft.documents }));
    } catch (err) {
      showAppAlert({ type: 'error', title: 'Delete Failed', message: `Failed to delete document: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handlePrimaryImageUpload = async (file) => {
    if (!file) return;
    try {
      setSaving(true);
      const updatedDraft = await ownerService.uploadPrimaryImage(formData.id, file);
      setFormData(prev => ({ ...prev, primaryImageUrl: updatedDraft.primaryImageUrl }));
    } catch (err) {
      showAppAlert({ type: 'error', title: 'Upload Failed', message: `Failed to upload primary image: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleGalleryImagesUpload = async (files) => {
    if (!files?.length) return;
    try {
      setSaving(true);
      const updatedDraft = await ownerService.uploadImages(formData.id, files);
      setFormData(prev => ({ ...prev, images: updatedDraft.images || [] }));
    } catch (err) {
      showAppAlert({ type: 'error', title: 'Upload Failed', message: `Failed to upload images: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteGalleryImage = async (imageId) => {
    try {
      setSaving(true);
      const updatedDraft = await ownerService.deleteImage(formData.id, imageId);
      setFormData(prev => ({ ...prev, images: updatedDraft.images || [] }));
    } catch (err) {
      showAppAlert({ type: 'error', title: 'Delete Failed', message: `Failed to delete image: ${err.message}` });
    } finally {
      setSaving(false);
    }
  };

  const toggleFeature = (specId) => {
     setFormData(prev => {
        const specIds = [...prev.specificationIds];
        if (specIds.includes(specId)) {
           return { ...prev, specificationIds: specIds.filter(id => id !== specId) };
        } else {
           specIds.push(specId);
           return { ...prev, specificationIds: specIds };
        }
     });
  };

  if (isSuccess) {
    return (
      <div className="flex h-screen bg-[#F8F9FA] font-sans text-[#1F1F1F] overflow-hidden">
        <Sidebar activePage="Fleet" />
        <main className="flex-grow flex items-center justify-center p-10 bg-white">
          <div className="max-w-md w-full text-center space-y-8 animate-in zoom-in duration-700">
            <div className="w-24 h-24 bg-green-500 rounded-2xl flex items-center justify-center mx-auto shadow-2xl shadow-green-200 rotate-12">
               <Check className="w-12 h-12 text-white" />
            </div>
            <div className="space-y-4">
               <h1 className="text-4xl font-black tracking-tight">Vehicle Listed!</h1>
               <p className="text-gray-400 font-bold uppercase tracking-widest text-[10px]">Your listing is now pending approval</p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-8 space-y-4">
               <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                  <span className="text-[10px] font-black uppercase text-gray-400">Reference ID</span>
                  <span className="text-sm font-bold truncate max-w-[150px]">#{formData.id}</span>
               </div>
               <div className="flex items-center justify-between">
                  <span className="text-[10px] font-black uppercase text-gray-400">Status</span>
                  <span className="text-xs font-black text-yellow-600 bg-yellow-50 px-3 py-1 rounded-full uppercase">Pending Approval</span>
               </div>
            </div>
            <div className="grid grid-cols-1 gap-4">
               <button onClick={() => navigate('/owner-dashboard')} className="w-full bg-brand-black text-white py-5 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-800 transition-all shadow-xl shadow-gray-200">
                  Return to Dashboard
               </button>
               <button onClick={() => setIsSuccess(false) || setCurrentStep(1) || setFormData({...formData, id: null, makeId: '', model: ''})} className="w-full bg-white text-brand-black border border-gray-100 py-5 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-gray-50 transition-all">
                  Add Another Vehicle
               </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
     return <div className="flex h-screen items-center justify-center bg-[#F8F9FA]"><div className="w-10 h-10 border-4 border-brand-black border-t-transparent rounded-full animate-spin"></div></div>;
  }

  return (
    <div className="flex h-screen bg-[#F8F9FA] font-sans text-[#1F1F1F] overflow-hidden">
      <Sidebar activePage="Fleet" />
      <main className="flex-grow overflow-y-auto">
        <header className="bg-white border-b border-gray-100 flex items-center justify-between px-10 py-5 sticky top-0 z-20">
          <div className="relative w-96">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search fleet or bookings..." className="w-full pl-12 pr-4 py-3 bg-gray-50 border-transparent rounded-xl text-sm focus:bg-white focus:ring-2 focus:ring-[#111111] focus:border-transparent transition-all outline-none" />
          </div>
          <div className="flex items-center gap-6">
            <button className="relative p-2 text-gray-400 hover:text-brand-black transition-all">
              <Bell className="w-5 h-5" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
            </button>
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center font-black text-xs text-gray-500">AV</div>
          </div>
        </header>

        <div className="p-10">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
               <div className="px-12 pt-12 pb-8 border-b border-gray-50 bg-gray-50/30">
                  {formData.status === 'REJECTED' && formData.rejectionReason && (
                    <div className="mb-8 rounded-2xl border border-red-100 bg-red-50 px-6 py-5">
                      <p className="text-[10px] font-black uppercase tracking-widest text-red-500 block mb-2">What to change before resubmitting</p>
                      <p className="text-sm font-bold text-red-800">{formData.rejectionReason}</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-8">
                     <div>
                        <span className="text-[10px] font-black transform uppercase tracking-[0.2em] text-brand-black block mb-1">Step {currentStep} of 3</span>
                        <h1 className="text-3xl font-black tracking-tight">Vehicle Details</h1>
                     </div>
                     <div className="text-right">
                        <span className="text-2xl font-black text-brand-black">{Math.round((currentStep / 3) * 100)}%</span>
                        <div className="w-32 h-2 bg-gray-100 rounded-full mt-2 overflow-hidden">
                           <div className="h-full bg-brand-orange-700 transition-all duration-500" style={{ width: `${(currentStep / 3) * 100}%` }}></div>
                        </div>
                     </div>
                  </div>

                  <div className="flex justify-between relative">
                     <div className="absolute top-1/2 left-0 w-full h-0.5 bg-gray-100 -translate-y-1/2 z-0"></div>
                     {steps.map((step) => (
                        <div key={step.id} className="relative z-10 flex flex-col items-center gap-3 bg-transparent">
                           <div className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-xs transition-all duration-300 ${currentStep === step.id ? 'bg-brand-black text-white shadow-lg scale-110' : currentStep > step.id ? 'bg-brand-orange-700 text-white' : 'bg-white text-gray-300 border-2 border-gray-100'}`}>
                              {currentStep > step.id ? <ShieldCheck className="w-5 h-5" /> : step.id}
                           </div>
                           <span className={`text-[9px] font-black uppercase tracking-widest ${currentStep === step.id ? 'text-brand-black' : 'text-gray-400'}`}>{step.label}</span>
                        </div>
                     ))}
                  </div>
               </div>

                <div className="p-12">
                   {currentStep === 1 && (
                     <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Car Make</label>
                              <select
                                value={formData.makeId}
                                onChange={(e) => setFormData({...formData, makeId: e.target.value})}
                                className="w-full p-4 bg-gray-50 border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none">
                                 <option value="">Select Brand</option>
                                 {options.makes.map(m => (
                                    <option key={m.id} value={m.id}>{m.name}</option>
                                 ))}
                              </select>
                           </div>
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Model</label>
                              <input
                                type="text"
                                value={formData.model}
                                onChange={(e) => setFormData({...formData, model: e.target.value})}
                                placeholder="e.g. Model S, X6, Camry"
                                className="w-full p-4 bg-gray-50 border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                              />
                           </div>
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Plate Number</label>
                              <input
                                type="text"
                                value={formData.plateNumber}
                                onChange={(e) => setFormData({...formData, plateNumber: e.target.value.toUpperCase()})}
                                placeholder="e.g. RAD 123 A"
                                className="w-full p-4 bg-gray-50 border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                              />
                           </div>
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Year</label>
                              <select
                                value={formData.year}
                                onChange={(e) => setFormData({...formData, year: e.target.value})}
                                className="w-full p-4 bg-gray-50 border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none">
                                 {['2024','2023','2022','2021','2020','2019'].map(y => <option key={y} value={y}>{y}</option>)}
                              </select>
                           </div>
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Odometer (Miles)</label>
                              <input
                                type="number"
                                value={formData.odometer}
                                onChange={(e) => setFormData({...formData, odometer: e.target.value})}
                                placeholder="Enter mileage"
                                className="w-full p-4 bg-gray-50 border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                              />
                           </div>
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Transmission</label>
                              <select
                                value={formData.transmission}
                                onChange={(e) => setFormData({...formData, transmission: e.target.value})}
                                className="w-full p-4 bg-gray-50 border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none">
                                 <option value="">Select Transmission</option>
                                 <option value="Automatic">Automatic</option>
                                 <option value="Manual">Manual</option>
                              </select>
                           </div>
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Fuel Type</label>
                              <select
                                value={formData.fuelType}
                                onChange={(e) => setFormData({...formData, fuelType: e.target.value})}
                                className="w-full p-4 bg-gray-50 border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none">
                                 <option value="">Select Fuel Type</option>
                                 <option value="Petrol">Petrol</option>
                                 <option value="Diesel">Diesel</option>
                                 <option value="Hybrid">Hybrid</option>
                                 <option value="EV">EV</option>
                              </select>
                           </div>
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Seats</label>
                              <input
                                type="number"
                                min="1"
                                value={formData.seats}
                                onChange={(e) => setFormData({...formData, seats: e.target.value})}
                                placeholder="e.g. 5"
                                className="w-full p-4 bg-gray-50 border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                              />
                           </div>
                        </div>

                        <div className="mb-12">
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Talk About The Car</label>
                              <textarea
                                rows="5"
                                value={formData.description}
                                onChange={(e) => setFormData({...formData, description: e.target.value})}
                                placeholder="Describe the condition, comfort, driving feel, standout features, and what makes this vehicle special for renters."
                                className="w-full p-4 bg-gray-50 border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none resize-none"
                              />
                           </div>
                        </div>

                        <div className="mb-12">
                           <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1 block mb-4">Vehicle Category</label>
                           <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                              {options.categories.map((cat) => (
                                 <button
                                   key={cat.id}
                                   onClick={() => setFormData({...formData, categoryId: cat.id})}
                                   className={`p-6 rounded-2xl border-2 flex flex-col items-center gap-4 transition-all ${
                                     formData.categoryId === cat.id ? 'border-brand-black bg-brand-black text-white shadow-xl translate-y-[-4px]' : 'border-gray-50 bg-gray-50 text-gray-400 hover:border-gray-200'
                                   }`}
                                 >
                                    <Car className="w-6 h-6" />
                                    <span className="text-[10px] font-black uppercase tracking-widest">{cat.name}</span>
                                 </button>
                              ))}
                           </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                           <div className="space-y-2">
                              <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Car Location City</label>
                              <select
                                value={formData.cityId}
                                onChange={(e) => setFormData({...formData, cityId: e.target.value})}
                                className="w-full p-4 bg-gray-50 border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none">
                                 <option value="">Select City</option>
                                 {options.cities.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                 ))}
                              </select>
                           </div>
                        </div>
                     </div>
                   )}

                   {currentStep === 2 && (
                     <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                        <section className="mb-12">
                           <h3 className="text-xl font-black mb-8 flex items-center gap-3"><Wallet className="w-6 h-6 text-brand-black" />Pricing Structure</h3>
                           <div className="bg-gray-50 border border-gray-100 rounded-3xl p-6 mb-8">
                              <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mb-4">Choose pricing tier and add amount</p>
                              <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr_auto] gap-4 items-end">
                                 <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Pricing Name</label>
                                    <select
                                      value={selectedPricingType}
                                      onChange={(e) => setSelectedPricingType(e.target.value)}
                                      disabled={!availablePricingOptions.length}
                                      className="w-full p-4 bg-white border border-gray-200 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                                    >
                                      {!availablePricingOptions.length && (
                                        <option value="">All pricing tiers have been added</option>
                                      )}
                                      {availablePricingOptions.map((option) => (
                                        <option key={option.pricingType} value={option.pricingType}>
                                          {option.label} ({option.rangeLabel})
                                        </option>
                                      ))}
                                    </select>
                                 </div>
                                 <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Price Amount</label>
                                    <div className="relative">
                                       <span className="absolute left-4 top-1/2 -translate-y-1/2 font-black text-gray-400">RF</span>
                                       <input
                                         type="number"
                                         min="0"
                                         value={newPricingAmount}
                                         onChange={(e) => setNewPricingAmount(e.target.value)}
                                         className="w-full p-4 pl-12 bg-white border border-gray-200 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                                         placeholder="Enter amount"
                                       />
                                    </div>
                                 </div>
                                 <button
                                   type="button"
                                   onClick={handleAddPricingConfiguration}
                                   disabled={!availablePricingOptions.length}
                                   className="px-6 py-4 rounded-2xl bg-brand-black text-white text-[10px] font-black uppercase tracking-widest"
                                 >
                                   Add Pricing
                                 </button>
                              </div>
                           </div>

                           <div className="space-y-3 mb-8">
                              {formData.pricing.pricingConfigurations.length ? formData.pricing.pricingConfigurations.map((item) => (
                                <div key={item.pricingType} className="rounded-3xl border border-gray-100 bg-white p-5 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                  <div>
                                    <p className="text-sm font-black text-brand-black">{item.label}</p>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400 mt-1">{item.rangeLabel}</p>
                                  </div>
                                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                                    <div className="relative min-w-[220px]">
                                      <span className="absolute left-4 top-1/2 -translate-y-1/2 font-black text-gray-400">RF</span>
                                      <input
                                        type="number"
                                        min="0"
                                        value={item.amount}
                                        onChange={(e) => handlePricingAmountChange(item.pricingType, e.target.value)}
                                        className="w-full p-4 pl-12 bg-gray-50 border border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                                      />
                                    </div>
                                    <span className="px-4 py-3 rounded-2xl bg-gray-100 text-[10px] font-black uppercase tracking-widest text-gray-500">
                                      {item.perDay ? 'Charged Per Day' : 'Package Amount'}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() => handleRemovePricingConfiguration(item.pricingType)}
                                      className="px-4 py-3 rounded-2xl border border-red-100 bg-red-50 text-red-600 text-[10px] font-black uppercase tracking-widest"
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              )) : (
                                <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 px-5 py-6 text-sm font-bold text-gray-500">
                                  No pricing tiers added yet. Add all four pricing tiers before continuing.
                                </div>
                              )}
                           </div>

                           <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                              <div className="space-y-2">
                                 <label className="text-[10px] font-black uppercase tracking-widest text-gray-400 ml-1">Daily Price (Outside City)</label>
                                 <div className="relative">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 font-black text-gray-400 pointer-events-none">RF</span>
                                    <input
                                      type="number"
                                      value={formData.pricing.outsideCityPrice}
                                      onChange={(e) => setFormData({...formData, pricing: {...formData.pricing, outsideCityPrice: e.target.value}})}
                                      className="w-full p-4 pl-12 pr-12 bg-gray-50 border-gray-100 rounded-2xl text-sm font-bold focus:bg-white focus:ring-2 focus:ring-[#111111] transition-all outline-none"
                                    />
                                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400 uppercase">/ Day</span>
                                 </div>
                                 <p className="text-[11px] text-gray-500 font-bold leading-5 ml-1">
                                    This amount is added on top of the selected Pricing Structure tier whenever the renter chooses an outside-city trip.
                                 </p>
                              </div>
                           </div>

                           <div className="bg-brand-orange-50/30 rounded-2xl p-8 border border-brand-orange-100/50 flex flex-col sm:flex-row items-center justify-between gap-6">
                              <div className="flex items-center gap-5">
                                 <div className="w-14 h-14 rounded-2xl bg-white shadow-lg flex items-center justify-center">
                                    <ShieldCheck className="w-7 h-7 text-brand-black" />
                                 </div>
                                 <div>
                                    <h4 className="font-black text-sm">Professional Driver Service</h4>
                                    <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mt-0.5">Recommended for high-end luxury rentals</p>
                                 </div>
                              </div>
                              <div className="flex items-center gap-6">
                                 <button
                                   onClick={() => setFormData({...formData, pricing: {...formData.pricing, professionalDriver: !formData.pricing.professionalDriver}})}
                                   className={`w-14 h-8 rounded-full transition-all relative ${formData.pricing.professionalDriver ? 'bg-brand-orange-700' : 'bg-gray-200'}`}
                                 >
                                    <div className={`absolute top-1 w-6 h-6 rounded-full bg-white transition-all shadow-sm ${formData.pricing.professionalDriver ? 'left-7' : 'left-1'}`}></div>
                                 </button>
                              </div>
                           </div>
                        </section>

                        <section>
                           <h3 className="text-xl font-black mb-8 flex items-center gap-3"><PlusCircle className="w-6 h-6 text-brand-black" />Vehicle Specifications</h3>
                           <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                              {options.specifications.map((spec) => {
                                 const isSelected = formData.specificationIds.includes(spec.id);
                                 return (
                                    <label
                                      key={spec.id}
                                      onClick={() => toggleFeature(spec.id)}
                                      className={`p-5 rounded-2xl border-2 flex items-center justify-between cursor-pointer transition-all ${
                                        isSelected ? 'border-brand-black bg-brand-black text-white' : 'border-gray-50 bg-gray-50 text-gray-400 hover:border-gray-200'
                                      }`}
                                    >
                                       <div className="flex items-center gap-3">
                                          <span className="text-[10px] font-black uppercase tracking-widest">{spec.name}</span>
                                       </div>
                                       <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                          isSelected ? 'bg-brand-orange-700 border-brand-orange-600' : 'border-gray-200 bg-white'
                                       }`}>
                                          {isSelected && <Check className="w-3 h-3 text-white" />}
                                       </div>
                                    </label>
                                 );
                              })}
                           </div>
                        </section>
                     </div>
                   )}

                    {currentStep === 3 && (
                     <div className="animate-in fade-in slide-in-from-right-4 duration-500">
                        <section className="mb-12">
                           <h3 className="text-xl font-black mb-2 flex items-center gap-3"><Camera className="w-6 h-6 text-brand-black" />Primary Car Photo <span className="text-red-500">*</span></h3>
                           <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3 font-bold mb-6">
                              Please include clear interior images as part of your car photos so renters can see the seats, dashboard, and cabin condition.
                           </p>
                           <div className="border-4 border-dashed border-gray-100 rounded-2xl p-12 text-center mb-8 bg-gray-50/30">
                              {formData.primaryImageUrl ? (
                                 <div className="flex flex-col items-center gap-4">
                                    <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-2">
                                       <Check className="w-12 h-12" />
                                    </div>
                                    <h4 className="font-black text-lg text-green-600">Primary Image Uploaded</h4>
                                    <p className="text-xs text-gray-400 font-bold truncate">{formData.primaryImageUrl.split('/').pop()}</p>
                                    <label className="bg-white text-brand-black border border-gray-200 px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest cursor-pointer hover:bg-gray-50 shadow-sm mt-4">
                                       Replace Image
                                       <input type="file" className="hidden" accept="image/*" onChange={(e) => handlePrimaryImageUpload(e.target.files[0])} />
                                    </label>
                                 </div>
                              ) : (
                                 <div className="flex flex-col items-center gap-4">
                                    <div className="w-20 h-20 bg-white rounded-2xl shadow-lg flex items-center justify-center mx-auto mb-2">
                                       <Camera className="w-10 h-10 text-brand-black" />
                                    </div>
                                    <h4 className="text-lg font-black mb-1">Select Primary Image</h4>
                                    <p className="text-xs text-gray-400 font-bold max-w-sm mx-auto">This image will be displayed on the platform search results. Minimum 800x600 resolution recommended.</p>
                                    <label className="bg-brand-black text-white transition-all hover:bg-gray-800 px-8 py-3 rounded-xl text-xs font-black uppercase tracking-widest cursor-pointer mt-4">
                                       Upload File
                                       <input type="file" className="hidden" accept="image/*" onChange={(e) => handlePrimaryImageUpload(e.target.files[0])} />
                                    </label>
                                 </div>
                              )}
                           </div>
                        </section>

                        <section className="mb-12">
                           <div className="flex items-center justify-between gap-4 mb-6">
                              <div>
                                 <h3 className="text-xl font-black flex items-center gap-3"><Camera className="w-6 h-6 text-brand-black" />More Car Images</h3>
                                 <p className="text-[11px] text-gray-500 font-bold uppercase tracking-widest mt-2">Upload exterior and interior photos. You can add multiple images.</p>
                              </div>
                              <label className="bg-brand-black text-white transition-all hover:bg-gray-800 px-6 py-3 rounded-xl text-[10px] font-black uppercase tracking-widest cursor-pointer">
                                 Add Images
                                 <input
                                   type="file"
                                   className="hidden"
                                   accept="image/*"
                                   multiple
                                   onChange={(e) => handleGalleryImagesUpload(e.target.files)}
                                 />
                              </label>
                           </div>

                           {formData.images?.length ? (
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                                 {formData.images.map((image, index) => (
                                    <div key={image.id} className="rounded-2xl border border-gray-100 bg-white overflow-hidden shadow-sm">
                                       <div className="aspect-[4/3] bg-gray-100">
                                          <img
                                            src={buildPublicFileUrl(image.imageUrl)}
                                            alt={`Car gallery ${index + 1}`}
                                            className="w-full h-full object-cover"
                                          />
                                       </div>
                                       <div className="p-4 flex items-center justify-between gap-3">
                                          <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
                                             Image {index + 1}
                                          </span>
                                          <button
                                            onClick={() => handleDeleteGalleryImage(image.id)}
                                            className="text-red-600 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors"
                                          >
                                             Remove
                                          </button>
                                       </div>
                                    </div>
                                 ))}
                              </div>
                           ) : (
                              <div className="border border-dashed border-gray-200 rounded-2xl p-8 text-center bg-gray-50/40">
                                 <p className="text-sm font-black text-brand-black">No extra images uploaded yet</p>
                                 <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-2">
                                    Add side views, rear views, and interior images.
                                 </p>
                              </div>
                           )}
                        </section>

                        <section className="mb-12">
                           <h3 className="text-xl font-black mb-8 flex items-center gap-3"><FileText className="w-6 h-6 text-brand-black" />Vehicle Documents</h3>
                           <div className="space-y-4">
                              {options.requiredDocuments && options.requiredDocuments.map((reqDoc) => {
                                 const uploadedDoc = formData.documents.find(d => d.requiredDocumentId === reqDoc.id);
                                 return (
                                    <div key={reqDoc.id} className={`p-6 rounded-2xl border ${uploadedDoc ? 'bg-green-50/30 border-green-100 text-green-900' : 'bg-gray-50 border-transparent'} flex items-center justify-between`}>
                                       <div className="flex items-center gap-5">
                                          <div className={`w-12 h-12 rounded-2xl shadow-sm flex items-center justify-center ${uploadedDoc ? 'bg-green-500 text-white' : 'bg-white text-brand-black'}`}>
                                             {uploadedDoc ? <Check className="w-6 h-6" /> : <FileText className="w-6 h-6" />}
                                          </div>
                                          <div>
                                             <h4 className="font-black text-sm">{reqDoc.name} {reqDoc.isRequired && <span className="text-red-500 ml-1">*</span>}</h4>
                                             {uploadedDoc ? (
                                                <p className="text-[10px] text-green-600 font-bold uppercase tracking-widest mt-0.5">Uploaded Successfully</p>
                                             ) : (
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">PDF, JPG (Max 5MB)</p>
                                             )}
                                          </div>
                                       </div>
                                       <div className="flex items-center gap-3">
                                          {uploadedDoc ? (
                                             <>
                                                <button onClick={() => window.open(buildPublicFileUrl(uploadedDoc.filePath), '_blank')} className="text-brand-black bg-brand-orange-50 hover:bg-brand-orange-100 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors">
                                                   Download
                                                </button>
                                                <button onClick={() => handleDeleteDocument(uploadedDoc.id)} className="text-red-600 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-colors">
                                                   Remove
                                                </button>
                                             </>
                                          ) : (
                                             <label className="bg-white text-brand-black border border-gray-100 cursor-pointer px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-gray-50 shadow-sm transition-all focus-within:ring-2 focus-within:ring-brand-orange-500">
                                                Upload
                                                <input type="file" className="hidden" onChange={(e) => handleUploadDocument(reqDoc.id, e.target.files[0])} />
                                             </label>
                                          )}
                                       </div>
                                    </div>
                                 );
                              })}
                           </div>
                        </section>
                     </div>
                   )}

                   <div className="flex items-center justify-between pt-12 mt-12 border-t border-gray-50">
                      <button onClick={handleBack} disabled={saving} className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-gray-400 hover:text-black transition-colors disabled:opacity-50">
                         <ChevronLeft className="w-4 h-4" /> Back
                      </button>
                      <div className="flex gap-4">
                         <button disabled={saving} onClick={handleNext} className="bg-brand-black text-white px-10 py-4 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center gap-3 hover:bg-gray-800 transition-all shadow-xl shadow-gray-200 disabled:opacity-50">
                           {saving ? 'Saving...' : (currentStep === 3 ? (formData.status === 'REJECTED' ? 'Update & Resubmit Car' : 'Finish & Post Car') : 'Save & Continue')}
                           {!saving && <ChevronRight className="w-4 h-4" />}
                         </button>
                      </div>
                   </div>
                </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AddVehicle;
